import React, { useCallback, useState } from 'react';
import type { FormikErrors } from 'formik';
import styled from 'styled-components';
import { Button, IconButton } from '@workboard/wobo-ui';
import { Remove } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { ItemContainer } from './InlineField';
import { UniversalField, UniversalFieldDef } from './UniversalField';
import { ArrayField, RenderFormProps, RenderLineProps } from './ArrayField';

const InlineFormButtons = styled.div`
  margin-top: 8px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const FieldWrapper = styled.div`
  width: ${(props) => 100 / props.items}%;
  padding: 8px;
`;

const Error = styled.div`
  font-size: 14px;
  -webkit-letter-spacing: 0.15px;
  -moz-letter-spacing: 0.15px;
  -ms-letter-spacing: 0.15px;
  letter-spacing: 0.15px;
  line-height: 1.43rem;
  color: #d93630;
  font-family: Lato, Helvetica Neue, sans-serif;
`;

function DefaultLineActions({
  remove,
  isDisabled,
  id,
}: RenderLineProps<unknown>) {
  const { t } = useTranslation();
  return (
    <IconButton
      title={t('Remove')}
      aria-label={t('Remove item')}
      disabled={isDisabled}
      onClick={() => remove(id)}
      size="small"
      type="button"
      variant="ghost"
    >
      <Remove />
    </IconButton>
  );
}

export type ArrayFieldDef<V, C = undefined> = Omit<
  UniversalFieldDef<V, C>,
  'onChange' | 'isError' | 'isDisabled' | 'context'
> & {
  renderValue?: (props: RenderLineProps<V>) => React.ReactChild;
};

type RenderSimpleFormProps<V> = RenderFormProps<V> & {
  validate?: (formData: Partial<V>) => FormikErrors<V>;
};

const SimpleArrayForm = <V,>({
  fields,
  validate,
  hide,
  add,
}: RenderSimpleFormProps<V> & { fields: ArrayFieldDef<any>[] }) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<Partial<V>>({});
  const [errors, setErrors] = useState<FormikErrors<Partial<V>>>({});
  const isValid = Object.values(errors).length === 0;

  const setValue = useCallback(
    <K extends keyof V>(name: K, val: V[K]) => {
      setValues((vals) => {
        const newVals = {
          ...vals,
          [name]: val,
        };
        setErrors(validate?.(newVals) ?? {});
        return newVals;
      });
    },
    [setValues, validate]
  );
  return (
    <>
      <ItemContainer items={fields.length}>
        {fields.map(({ ...field }) => (
          <UniversalField<any, Partial<V>>
            key={field.name}
            {...field}
            isError={Boolean(errors?.[field.name])}
            helperText={errors?.[field.name] ?? field.helperText}
            value={values?.[field.name] ?? field.value}
            context={values}
            onChange={(_, val) => setValue(field.name as any, val)}
          />
        ))}
      </ItemContainer>
      <InlineFormButtons>
        <Button size="small" type="reset" variant="ghost" onClick={hide}>
          {t('Cancel')}
        </Button>
        <Button
          size="small"
          type="submit"
          variant="primary"
          disabled={!isValid}
          onClick={() => {
            if (isValid) {
              add(values as any);
              hide();
              setValues({});
            }
          }}
        >
          {t('Add')}
        </Button>
      </InlineFormButtons>
    </>
  );
};

export interface SimpleArrayFieldProps<V> {
  id: string;
  name: string;
  value: V[];
  onChange(e: React.ChangeEvent<unknown>, val: V[]): void;
  errors?: Array<FormikErrors<V>>;
  isDisabled?: boolean;
  fields: ArrayFieldDef<any>[];
  renderLineActions?: (props: RenderLineProps<V>) => React.ReactChild;
  validateRecord?: (formData: Partial<V>) => FormikErrors<V>;
}

export const SimpleArrayField = <V extends Record<string, unknown>>({
  fields,
  renderLineActions,
  validateRecord,
  ...props
}: SimpleArrayFieldProps<V>) => {
  const renderLine = useCallback(
    (helpers) => (
      <>
        {fields.map(({ renderValue, ...field }) => {
          const error = helpers.error?.[field.name];
          return (
            <FieldWrapper key={field.name} items={fields.length}>
              {renderValue?.(helpers) ?? helpers.value[field.name].label}
              {error ? <Error>{error}</Error> : null}
            </FieldWrapper>
          );
        })}
        {renderLineActions?.(helpers) || <div><DefaultLineActions {...helpers} /></div>}
      </>
    ),
    [fields, renderLineActions]
  );
  const renderForm = useCallback(
    (formProps: RenderFormProps<V>) => (
      <SimpleArrayForm<V>
        {...formProps}
        fields={fields}
        validate={validateRecord}
      />
    ),
    [fields, validateRecord]
  );

  return (
    <ArrayField renderLine={renderLine} renderForm={renderForm} {...props} />
  );
};
